/**
 * AwsServiceSelect
 * ================
 * A small accessible dropdown that shows the official-style AWS service icon
 * (via <AwsServiceIcon/>) next to each option. Native <select>/<option> cannot
 * render SVG/React content, so this is a custom listbox used where we want the
 * real AWS icons in the picker (e.g. the alarm-creation service selector).
 *
 * Controlled component:
 *   <AwsServiceSelect
 *      options={[{ value: 'lambda', label: 'AWS Lambda' }, ...]}
 *      value={formData.service}
 *      onChange={(val) => handleInputChange('service', val)} />
 *
 * Each option's `value` is used as the AwsServiceIcon service key. Accessibility:
 * button has aria-haspopup/aria-expanded; list is role=listbox with role=option
 * rows; supports keyboard (Enter/Space/ArrowUp/ArrowDown/Escape) and closes on
 * outside click or blur.
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import AwsServiceIcon from './AwsServiceIcon';
import './AwsServiceSelect.css';

const AwsServiceSelect = ({ options = [], value, onChange, id, className = '' }) => {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(() =>
    Math.max(0, options.findIndex((o) => o.value === value))
  );
  const rootRef = useRef(null);
  const listRef = useRef(null);

  const selected = options.find((o) => o.value === value) || options[0] || null;

  const close = useCallback(() => setOpen(false), []);

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        close();
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open, close]);

  // Keep the active (highlighted) option in sync when opening.
  useEffect(() => {
    if (open) {
      setActiveIndex(Math.max(0, options.findIndex((o) => o.value === value)));
    }
  }, [open, value, options]);

  const choose = (opt) => {
    if (opt && onChange) onChange(opt.value);
    close();
  };

  const onKeyDown = (e) => {
    if (!open) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(options.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      choose(options[activeIndex]);
    }
  };

  return (
    <div className={`aws-service-select ${className}`.trim()} ref={rootRef}>
      <button
        type="button"
        id={id}
        className="aws-service-select__button form-select"
        aria-haspopup="listbox"
        aria-expanded={open ? 'true' : 'false'}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onKeyDown}
      >
        <span className="aws-service-select__value">
          {selected && <AwsServiceIcon service={selected.value} size={20} />}
          <span>{selected ? selected.label : 'Select service…'}</span>
        </span>
        <span className="aws-service-select__chevron" aria-hidden="true">▾</span>
      </button>

      {open && (
        <ul
          className="aws-service-select__list"
          role="listbox"
          ref={listRef}
          tabIndex={-1}
        >
          {options.map((opt, idx) => (
            <li
              key={opt.value}
              role="option"
              aria-selected={opt.value === value ? 'true' : 'false'}
              className={
                'aws-service-select__option' +
                (opt.value === value ? ' is-selected' : '') +
                (idx === activeIndex ? ' is-active' : '')
              }
              onMouseEnter={() => setActiveIndex(idx)}
              onClick={() => choose(opt)}
            >
              <AwsServiceIcon service={opt.value} size={20} />
              <span>{opt.label}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AwsServiceSelect;
